import { without } from 'lodash';
import { labeled, enumRequired, stringRequired } from '../../util/joi';
import { LevelOption, levelOptions } from '../global/LevelOption.enum';
import { YesNoOption, yesNoOptions } from '../global/YesNoOption.enum';

export const assetFinanceSchema = (Joi: any, label: string) => {
  const availableLevelOptions = without(levelOptions, LevelOption.none.name);
  const availableYesNoOptions = without(
    yesNoOptions,
    YesNoOption.none.name,
    YesNoOption.yes_discharged.name,
    YesNoOption.not_important.name,
    YesNoOption.unsure.name
  );

  return Joi.object()
    .keys({
      levelOfFinancialExperience: enumRequired(
        Joi,
        availableLevelOptions,
        labeled(label, 'What is your level of financial experience?')
      ),
      concernedAboutYourCurrentJobSecurity: enumRequired(
        Joi,
        availableLevelOptions,
        labeled(label, 'What is your level of concern about your current job security?')
      ),
      haveAnEmergencyFund: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Do you have an emergency fund, liquid asset or insurance to assist with repayment of debt in the event of a loss of income?'
        )
      ),
      ableToMaintainYourCommitmentsAndLifestyle: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Would you be able to maintain your commitments & lifestyle if you or your partner were unable to earn an income?'
        )
      ),
      haveAdequateInsurance: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Do you have adequate insurance to meet your loan repayments in the event you are unable to work? (e.g. life insurance, loan protection insurance, income protection insurance)'
        )
      ),
      awareOfAnyCircumstances: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Are you aware of any circumstances that may impact upon your ability to meet your financial commitments?'
        )
      )
    })
    .when('.concernedAboutYourCurrentJobSecurity.name', {
      is: LevelOption.high.name,
      then: Joi.object().keys({
        concernedAboutYourCurrentJobSecurityComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'What is your level of concern about your current job security? - Comments'
          )
        )
      })
    })
    .when('.awareOfAnyCircumstances.name', {
      is: YesNoOption.yes.name,
      then: Joi.object().keys({
        awareOfAnyCircumstancesComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'Are you aware of any circumstances that may impact upon your ability to meet your financial commitments? - Comments'
          )
        )
      })
    });
};

export const homeLoanSchema = (Joi: any, label: string) => {
  const availableLevelOptions = without(levelOptions, LevelOption.none.name);
  const availableYesNoOptions = without(
    yesNoOptions,
    YesNoOption.none.name,
    YesNoOption.yes_discharged.name,
    YesNoOption.not_important.name
  );

  return assetFinanceSchema(Joi, label)
    .keys({
      concernedAboutMovementsInInterestRates: enumRequired(
        Joi,
        availableLevelOptions,
        labeled(label, 'What is your level of concern about movements in interest rates?')
      ),
      importantFlexibilityOfLoan: enumRequired(
        Joi,
        availableLevelOptions,
        labeled(
          label,
          'How important is loan flexibility to you? (e.g. ability to redraw on additional repayments or to have an Offset Account to offset savings against loan interest, pay off the loan faster, etc)'
        )
      ),
      concernedAboutNegativePropertyValue: enumRequired(
        Joi,
        availableLevelOptions,
        labeled(
          label,
          'What is your level of concern about negative property value fluctuations?'
        )
      ),
      haveAdequateInsuranceComments: stringRequired(Joi, 1, 4096).label(
        labeled(
          label,
          'Do you have adequate insurance to meet your loan repayments in the event you are unable to work? (e.g. life insurance, loan protection insurance, income protection insurance) - Comments'
        )
      ),
      haveWill: enumRequired(Joi, availableYesNoOptions, labeled(label, 'Do you have a will?'))
    })
    .when('.concernedAboutMovementsInInterestRates.name', {
      is: LevelOption.high.name,
      then: Joi.object().keys({
        concernedAboutMovementsInInterestRatesComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'What is your level of concern about movements in interest rates? - Comments'
          )
        )
      })
    })
    .when('.importantFlexibilityOfLoan.name', {
      is: LevelOption.high.name,
      then: Joi.object().keys({
        importantFlexibilityOfLoanComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'How important is loan flexibility to you? (e.g. ability to redraw on additional repayments or to have an Offset Account to offset savings against loan interest, pay off the loan faster, etc) - Comments'
          )
        )
      })
    })
    .when('.concernedAboutNegativePropertyValue.name', {
      is: LevelOption.high.name,
      then: Joi.object().keys({
        concernedAboutNegativePropertyValueComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'What is your level of concern about negative property value fluctuations? - Comments'
          )
        )
      })
    });
};

export default {
  assetFinanceSchema,
  homeLoanSchema
};
